import { useState } from "react";
import { IconHelp, IconChevronDown } from "./icons";
import styles from "./OrderSummary.module.css";

// Sticky right-rail summary on the cart page. The accordions (shipping
// estimate, promo code, tax exemption) are visual stubs for now — the
// promo + tax fields just hold local state until the BFF wires them up.
export default function OrderSummary({ subtotal, lines = [], onCheckout, formatAmount, disabled = false, disabledReason = null }) {
  const [open, setOpen] = useState({ shipping: true, promo: false, tax: false });
  const [zip, setZip] = useState("");
  const [shipping, setShipping] = useState(null);
  const [promo, setPromo] = useState("");
  const [taxExempt, setTaxExempt] = useState("");

  const totalQty = lines.reduce((n, l) => n + (l.qty || 0), 0);

  function estimate(e) {
    e.preventDefault();
    if (!/^\d{5}(-\d{4})?$/.test(zip.trim())) return;
    // Stub estimate — flat rate so the UI shows a number until BFF lands.
    setShipping({ amount: 14.95, days: "3–5 business" });
  }

  return (
    <div className={styles.card}>
      <h2 className={styles.heading}>Order Summary</h2>
      <div className={styles.subtotalRow}>
        <span>{totalQty} ITEM{totalQty === 1 ? "" : "S"}</span>
        <strong>{formatAmount(subtotal)}</strong>
      </div>
      <div className={styles.subtotalNote}>Subtotal does not include shipping or tax</div>

      <Accordion
        title="Estimate Tax & Shipping"
        hint
        open={open.shipping}
        onToggle={() => setOpen((o) => ({ ...o, shipping: !o.shipping }))}
      >
        <p className={styles.muted}>Shipping available only to United States.</p>
        <form onSubmit={estimate} className={styles.estimateForm}>
          <input
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="Enter your ZIP code"
            className={styles.input}
            inputMode="numeric"
            pattern="\d{5}(-\d{4})?"
            aria-label="ZIP code"
          />
          <button type="submit" className={styles.btnDark}>Estimate</button>
        </form>
        {shipping && (
          <div className={styles.estimateResult}>
            Estimated: <strong>{formatAmount(shipping.amount)}</strong> · {shipping.days}
          </div>
        )}
      </Accordion>

      <Accordion
        title="Promo Code"
        open={open.promo}
        onToggle={() => setOpen((o) => ({ ...o, promo: !o.promo }))}
      >
        <div className={styles.estimateForm}>
          <input
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
            placeholder="Enter promo code"
            className={styles.input}
            aria-label="Promo code"
          />
          <button type="button" className={styles.btnDark}>Apply</button>
        </div>
      </Accordion>

      <Accordion
        title="Tax Exemption"
        open={open.tax}
        onToggle={() => setOpen((o) => ({ ...o, tax: !o.tax }))}
      >
        <div className={styles.estimateForm}>
          <input
            value={taxExempt}
            onChange={(e) => setTaxExempt(e.target.value)}
            placeholder="Tax exempt certificate #"
            className={styles.input}
            aria-label="Tax exempt"
          />
          <button type="button" className={styles.btnDark}>Submit</button>
        </div>
      </Accordion>

      <button
        type="button"
        className={styles.checkoutBtn}
        onClick={onCheckout}
        disabled={!lines.length || disabled}
        title={disabledReason || undefined}
      >
        Proceed to Checkout
      </button>
      {disabled && disabledReason && (
        <div className={styles.muted} style={{ textAlign: "center", marginTop: -4 }}>
          {disabledReason}
        </div>
      )}
    </div>
  );
}

function Accordion({ title, children, open, onToggle, hint }) {
  return (
    <section className={styles.acc}>
      <button
        type="button"
        className={styles.accHead}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={styles.accTitle}>
          {title} {hint && <IconHelp size={14} />}
        </span>
        <IconChevronDown
          size={18}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }}
        />
      </button>
      {open && <div className={styles.accBody}>{children}</div>}
    </section>
  );
}
